import React, { lazy, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { itemSearchTrack } from '../../analytics/tracker/events/menuEvents';
import debounce from '../../functions/debounce';
import { getAllPrices } from '../../functions/getPrice';
import { isVisibleToUser } from '../../functions/menuGrouping';
import { rcsTrack } from '../../functions/rcsTrack';
import { CloseIcon, emptyCart, leftarrow, search3 } from '../../paths/defaultImage';
import Recommendations from '../../recommendations';
import MenuPageFooter from '../appFooter/menuPageFooter';
import Card from '../common/card';
import NoItemFound from '../common/noItemFound';
import SuspenseWrapper from '../common/suspenseWrapper';
import LazyImage from '../common/lazyLoad';
const FullPopUp = lazy(() => import(/* webpackChunkName:"FullPopUp"*/ './fullPopUp'));
const callTracking = debounce(
  (value, length, userphone) => itemSearchTrack(value, length, userphone),
  3000
);
const rcsTracking = debounce((value) => rcsTrack('searchPage', { searchTerm: value }), 3000);
const OpenSearchPage = (props) => {
  const { t } = useTranslation();

  const [searchString, setSearchString] = useState('');
  const [searchMatched, setSearchMatched] = useState([]);
  const restarantSettingFromRedux = useSelector((state) => state.restSetting?.Setting);
  const { bestSellerItems, frequentlyAddWithItem } = useSelector((state) => state.recommendations);
  const cartFromRedux = useSelector((state) => state.cart.cart);
  const cartItemsIds = cartFromRedux?.cart_items?.map((item) => item.item_id);
  const bestSellerItemIds = bestSellerItems.map(({ id }) => id);
  const userphone = useSelector((state) => state.userDetails?.Details?.data?.phone);

  const inputRef = useRef(null);

  const addOnsId = useMemo(
    () => restarantSettingFromRedux?.addons?.map((addOn) => [...addOn?.itemids]).flat(1),
    [restarantSettingFromRedux]
  );

  const showOutOfStockEnabled =
    restarantSettingFromRedux?.settings?.global?.show_out_of_stock_enabled;
  const searchItemInMenu = (e) => {
    const value = e.target.value;
    setSearchString(value);
    const filter = props.menu?.filter((item) => {
      if (
        (showOutOfStockEnabled ||
          (!showOutOfStockEnabled && (item?.is_available == 1 || item?.is_available == 2))) &&
        isVisibleToUser(item.visible)
      ) {
        return (`(${item?.item_code}) ` + item?.name).toLowerCase().includes(value?.toLowerCase());
      }
    });
    setSearchMatched(filter);
    callTracking(value, filter?.length, userphone);
    rcsTracking(value);
  };
  const clearSearchHandler = () => {
    searchString && setSearchString('');
    setSearchMatched([]);
    inputRef.current.value = '';
  };
  // ios keyboard scroll to top
  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, [100]);
  }, []);
  return (
    <SuspenseWrapper>
      <FullPopUp
        height='100%'
        close={props.close}
        noCloseIcon
        noOverflowX={true}
        opacity={searchString ? '1' : ''}
        backgroundColor={searchString ? '' : 'rgba(0,0,0,0.5)'}>
        <div className='searchBar py-3 d-flex align-items-center justify-content-between gap-2 op-1'>
          <LazyImage src={leftarrow} alt='goBack' onClick={props.close} height={15} width={20} />
          <input
            className='flex-1 menu-search'
            type='text'
            id='menu_item_search_input'
            placeholder={t('what_would_you')}
            value={searchString}
            onChange={searchItemInMenu}
            autoFocus
            autoComplete='off'
            ref={inputRef}
          />
          {inputRef.current?.value ? (
            <LazyImage
              width='18px'
              height='18px'
              onClick={clearSearchHandler}
              src={CloseIcon}
              alt='CloseIcon'
            />
          ) : (
            <LazyImage width='18px' height='18px' src={search3} alt='search' />
          )}
        </div>
        <div className='d-flex justify-content-center flex-column align-items-center my-5 bg-color'>
          <div className='row d-flex justify-content-start mb-0 w-100 fs-14 text-muted px-20'>
            {searchString && searchMatched?.length
              ? `${searchMatched.length} ${t('results_found')}`
              : ''}
          </div>
          <div className='menuCard searchItemsContainer safe-bottom-margin'>
            {searchString &&
              searchMatched.map((menuItem, index, array) => {
                const allPrices = getAllPrices(menuItem);
                const isItemInBestSeller = bestSellerItemIds.includes(menuItem.id);
                const showFBT =
                  cartItemsIds?.includes(menuItem.id) &&
                  frequentlyAddWithItem[menuItem.id]?.items?.length;
                return (
                  <>
                    <Card
                      key={`search_${index}`}
                      id={menuItem.id}
                      name={menuItem.name}
                      image={{
                        image_normal: menuItem.img,
                        image_40x40: menuItem.img_40x40,
                        image_175x175: menuItem.img_175x175,
                      }}
                      imageType={menuItem.image_type}
                      description={menuItem.item_desc}
                      allPrices={allPrices}
                      originalPrice={menuItem.original_price_text}
                      variations={menuItem.variations}
                      isCustomizable={
                        menuItem?.variations?.length > 0 || addOnsId.includes(menuItem.id)
                      }
                      addOnId={menuItem.addon_ids}
                      availability={menuItem.is_available}
                      itemStatus={menuItem.status}
                      itemCode={menuItem?.item_code}
                      from='search'
                      cat={menuItem.cat_name}
                      subCat={menuItem.subcat_name}
                      showBestSellerTag={isItemInBestSeller}
                      isLastElementOfMenu={index == array.length - 1 || showFBT}
                      recsType={isItemInBestSeller ? 'best_seller' : ''}
                      explicitlyTriggerRecs={isItemInBestSeller}
                      cardPadding='px-20 pt-3'
                    />
                    {showFBT && (
                      <Recommendations
                        title={`Frequently added with ${menuItem.name}`}
                        recs={frequentlyAddWithItem[menuItem.id]?.items}
                        showMarker
                        from='search'
                        recsType='frequently_bought_with'
                        parentItemName={menuItem.name}
                      />
                    )}
                  </>
                );
              })}
            {searchString && searchMatched.length == 0 && (
              <NoItemFound
                img={emptyCart}
                header={t('no_result_found')}
                subHeader={t('looks_no_matches')}
              />
            )}
          </div>
        </div>
        <div className='pageFooter z-index-9'>
          <MenuPageFooter />
        </div>
      </FullPopUp>
    </SuspenseWrapper>
  );
};

export default OpenSearchPage;
